import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "matching" }
const _hoisted_2 = { class: "top" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = { class: "matching-layout" }
const _hoisted_5 = ["innerHTML"]

import { ref, onMounted, onUnmounted, Ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/store'

import { marked } from 'marked'
import { ACTION } from '@gd/tracking'
import CMatchingSlot from '@/components/CMatchingSlot.vue'
import CMatchingInfo from '@/components/CMatchingInfo.vue'
import CHorizontalProgress from '@/components/CHorizontalProgress.vue'
import CErrorModal from '@/components/CErrorModal.vue'
import { TournamentTypeToName } from '@/model/TournamentType'
import { MatchingProgress } from '@/model/Matching'
import { setTimeout } from 'timers'
import { getTournamentType } from '@/store/lib/util'
import { GD } from '@gd/storage'
import { ZoneIdColorSchemeMap } from '@/model/ColorScheme'
import { ERROR_CODES } from '@/model/ErrorCodes'
import matchingSuccessSound from '@/lib/matchingSuccessSound'
import { jslog } from '@gd/jslog'
import { track } from '@/lib/track'


export default /*@__PURE__*/_defineComponent({
  __name: 'MMatching',
  props: {
    tId: {},
    zoneId: {}
  },
  setup(__props: any) {

const props = __props
const router = useRouter()
const store = useStore()
const { t } = useI18n()

/**
 * The matching module, actually just a large pending screen (user-facing part) but is wired
 * to MatchingWebsocketClient to initiate matching and then forward user to the game if
 * matching is successful.
 * tId property must be set for this module to work.
 */
const matchingState = MatchingProgress
const matchingProgress: Ref<MatchingProgress> = ref(MatchingProgress.LOADING)
const loadingIndicator = ref(true)
const showErrorModal = ref(false)

const storageGD: GD = store.getters.storageGD
const failReason = ref('') // unknown_error, server_sent_leave, server_sent_close

const headline = computed(() => {
  return marked(t('matchingView.main.headline'))
})

const avatarUrl = computed(() => {
  return store.state.user.avatarUrl
})

const colorSchema = computed(() => {
  return ZoneIdColorSchemeMap[props.zoneId]
})

const numberOfSlotsExclOwn = computed((): number => {
  return store.getters['matching/matchingData'].numberOfPlayers - 1
})

function closeErrorModal() {
  router.push(routeBackToOverview.value)
}

function md(s: string) {
  return marked(s)
}

const matchingErrorI18nKey = computed((): string => {
  switch (failReason.value) {
    case 'server_sent_closed':
      return 'server_sent_closed'
    default:
      return 'unknown_error'
  }
})

const routeBackToOverview = computed(() => {
  const tournamentType = getTournamentType(
    store.getters['matching/matchingData'].gametype,
    store.getters['matching/matchingData'].zoneId
  )
  const tournamentName = TournamentTypeToName[tournamentType]
  return '/tournaments/' + tournamentName
})

onMounted(() => {
  window.addEventListener('unload', handleUnload)
  showErrorModal.value = false
  loadingIndicator.value = true
  failReason.value = ''
  store
    .dispatch('matching/fetchMatching', props.tId)
    .then(() => {
      loadingIndicator.value = false
      store
        .dispatch('matching/startMatching')
        .then((startGameParams: any) => {
          track({
            key: 'matching',
            action: ACTION.startGame,
            gameType: store.getters['matching/matchingData'].gametype,
            tournamentId: store.getters['matching/matchingData'].tournamentId,
          })
          return matchingSucceeded(startGameParams)
        })
        .catch(matchingCancelled)
    })
    .catch((err: any) => {
      jslog.send({ error: err })
      if (err && err.errorCode === 401) {
        router.replace('/extern/login')
      } else {
        router.push(`/error/${(err && err.errorCode) || ERROR_CODES.UNKNOWN}/${props.zoneId}`)
      }
    })
  // thats only for testing to simulate a broken ws connection
  if (process.env.NODE_ENV !== 'production') {
    ;(window as any).GD.$toast.error(
      "<div class='t-text'>" +
        '<p><strong>DEVMODE</strong></p>' +
        '<p>Simulate matching errors</p>' +
        '<p style="display: flex;flex-direction: column;">' +
        '<button style="margin-bottom: .4em;">unknown_error</button>' +
        '<button style="margin-bottom: .4em;">server_sent_leave</button>' +
        '<button style="margin-bottom: .4em;">server_sent_closed</button>' +
        '</p>' +
        '</div>',
      {
        icon: 'icf-specialAutumn',
        onContent: (e: any) => {
          // detect that the button is clicked
          if (e.target && e.target.tagName === 'BUTTON') {
            store.dispatch('matching/exitMatching')
            matchingCancelled(e.target.textContent)
          } else {
            // stop propagation will prevent toast from self closing
            e.stopPropagation()
          }
        },
      }
    )
  }
})

function onCancel() {
  track({
    key: 'matching',
    gameType: store.getters['matching/matchingData'].gametype,
    tournamentId: store.getters['matching/matchingData'].tournamentId,
    action: ACTION.cancelMatching,
  })
  exitMatching()
}

onUnmounted(() => {
  exitMatching(false)
})

function handleUnload(e: Event) {
  exitMatching(false)
}

function exitMatching(navigateBack = true) {
  window.removeEventListener('unload', handleUnload)
  store
    .dispatch('matching/exitMatching')
    .then(() => {
      if (navigateBack) {
        router.push(routeBackToOverview.value)
      }
    })
    .catch((error) => {
      jslog.send({ error: error })
    })
}

function matchingCancelled(reason: string) {
  failReason.value = reason
  showErrorModal.value = true
  jslog.send({ name: 'tournaments-matching-cancelled', data: { reason, tid: props.tId } })
}

function matchingSucceeded(startGameParams: any) {
  matchingProgress.value = MatchingProgress.SUCCESS
  matchingSuccessSound.play()
  // before proceeding, wait a random seconds duration: if all joined/matched users would proceed in parallel,
  // there's a high chance of race conditions if N users are requesting to create the competition at the same time
  const waitUntilProceeding = (Math.floor(Math.random() * 3) + 1) * 1000
  setTimeout(() => {
    proceedToGame(startGameParams)
  }, waitUntilProceeding)
}

function proceedToGame({ config, digest }: { config: string; digest: string }) {
  // the play-game page expects to be navigated to with a POST. doing the POST with a fetch will not work because
  // that would not change the page and changing document.location afterwards won't work, too
  // therefore, we'll take a slightly crude approach: create a FORM element on the fly, instrument it and submit
  const proceedToGameForm = document.createElement('form')
  proceedToGameForm.setAttribute('method', 'POST')
  // TODO the game page url should not be hard-wired! should be passed in - as part of matchingData?
  proceedToGameForm.setAttribute('action', '/gd/tournaments/play.xhtml')

  proceedToGameForm.appendChild(toHiddenParamElement('config', config))
  proceedToGameForm.appendChild(toHiddenParamElement('digest', digest))

  proceedToGameForm.appendChild(
    toHiddenParamElement('canPlayHtml', (storageGD.supportedTechs.indexOf('html5_webgl') !== -1).toString())
  )
  /**
   * TODO can be removed ?
   */
  proceedToGameForm.appendChild(toHiddenParamElement('canPlayFlash', 'false'))

  proceedToGameForm.appendChild(toHiddenParamElement('newTournaments', 'true'))

  const formParent = document.querySelector('.matching')
  formParent.appendChild(proceedToGameForm)
  proceedToGameForm.submit()
}

function toHiddenParamElement(name: string, value: string): HTMLInputElement {
  const hiddenParamElement = document.createElement('input')
  hiddenParamElement.setAttribute('type', 'hidden')
  hiddenParamElement.setAttribute('name', name)
  hiddenParamElement.setAttribute('value', value)
  return hiddenParamElement
}

return (_ctx: any,_cache: any) => {
  const _component_c_loading_indicator = _resolveComponent("c-loading-indicator")!
  const _component_t_button = _resolveComponent("t-button")!
  const _component_t_container = _resolveComponent("t-container")!
  const _component_t_content = _resolveComponent("t-content")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_t_content, { main: "" }, {
      default: _withCtx(() => [
        _createVNode(_component_c_loading_indicator, {
          show: loadingIndicator.value,
          "data-qe-id": "loader-spinning"
        }, null, 8, ["show"]),
        _createVNode(_component_t_container, null, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _createElementVNode("div", {
                class: "loading-message",
                innerHTML: headline.value
              }, null, 8, _hoisted_3),
              (matchingProgress.value === _unref(matchingState).LOADING)
                ? (_openBlock(), _createBlock(_component_t_button, {
                    key: 0,
                    class: "exit-matching-button",
                    m: "",
                    onClick: onCancel,
                    "data-qe-id": "cancel-button"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('matchingView.button.cancel')), 1)
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createVNode(CMatchingSlot, {
                name: _unref(store).state.user.name,
                "user-avatar-url": avatarUrl.value,
                color: colorSchema.value,
                state: _unref(matchingState).SUCCESS
              }, null, 8, ["name", "user-avatar-url", "color", "state"]),
              _createVNode(CHorizontalProgress, {
                color: "midGreen",
                size: "L",
                class: _normalizeClass(['loading-progress', { hidden: matchingProgress.value !== _unref(matchingState).LOADING }])
              }, null, 8, ["class"]),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(numberOfSlotsExclOwn.value, (index) => {
                return (_openBlock(), _createBlock(CMatchingSlot, {
                  color: colorSchema.value,
                  key: index,
                  state: matchingProgress.value
                }, null, 8, ["color", "state"]))
              }), 128))
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    _createVNode(_component_t_container, null, {
      default: _withCtx(() => [
        _createVNode(CMatchingInfo)
      ]),
      _: 1
    }),
    (showErrorModal.value)
      ? (_openBlock(), _createBlock(CErrorModal, {
          key: 0,
          onClose: closeErrorModal
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", {
              innerHTML: md(_ctx.$t('matchingView.error.' + matchingErrorI18nKey.value))
            }, null, 8, _hoisted_5)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})